import axios from 'axios';
import { getAccessToken, setAccessToken, clearAccessToken } from '../utils/token-store';
import {
  handleRateLimitError,
  handleServerError,
  handleNetworkError,
} from '../utils/api-error-handlers';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let queue = [];

const redirectLogin = () => {
  clearAccessToken();
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthEndpoint =
      original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/logout');

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          queue.push((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            apiClient(original)
              .then(resolve)
              .catch(() => redirectLogin());
          });
        });
      }

      isRefreshing = true;
      try {
        const { data } = await axios.post(
          `${apiClient.defaults.baseURL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const token = data.token;
        setAccessToken(token);
        original.headers.Authorization = `Bearer ${token}`;
        const pendientes = queue;
        queue = [];
        pendientes.forEach((cb) => cb(token));
        return apiClient(original);
      } catch (e) {
        queue = [];
        redirectLogin();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response) {
      if (status === 429) {
        handleRateLimitError(error);
      } else if (status === 401) {
        redirectLogin();
      } else {
        handleServerError(error);
      }
    } else {
      handleNetworkError(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
